'use client';

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { FileText, Loader2, Lock, Quote, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { API_ENDPOINTS } from '@/lib/api/config';
import {
  DocumentPagesViewer,
  legacyViewerPage,
  ocrQueries,
  type ViewerPage
} from '@/features/ocr';
import { purifySearchSnippet } from '../utils';
import { highlightSegments } from '../utils/highlight-terms';
import { buildRateMeters, type RateMeter } from '../utils/rate-meters';
import { buildScoreExplanation } from '../utils/score-explanation';
import type { SearchChunkEvidence, SearchHit, SearchScoring } from '../types';

interface SearchResultDetailDialogProps {
  hit: SearchHit | null;
  /** Query-level ranking configuration, identical for every hit on the page. */
  scoring?: SearchScoring | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "The matched text inside this document" (OMA-639) — the passages the result
 * was built from, where they sit on the page, and why its percentage is what it
 * is.
 *
 * Two columns, as the design draws it: the passages and the rate on one side,
 * the document page on the other, linked BOTH ways — selecting a passage moves
 * the viewer to its page and spotlights its region, and clicking a region on
 * the page selects its passage. The page half reuses `DocumentPagesViewer`
 * verbatim (its own toolbar carries zoom/print/download) rather than growing a
 * second image viewer inside search.
 *
 * Strictly read-only: the passage text and every factor come from payload the
 * search endpoint already returned for this user, which the engine filtered by
 * their permission keys server-side. The page images are the one extra read,
 * through the OCR feature's own result query — the same authenticated endpoint
 * the OCR screens use, so a document the account may not open does not render
 * here either.
 *
 * Two things this panel deliberately does NOT do:
 *
 *   - It does not render the rate as `base × weight = contribution` summing to
 *     the total, the way the design draws it. No such weighted score exists in
 *     the pipeline — see `utils/rate-meters.ts`. The panel keeps the design's
 *     layout and states what each number really is, including that the final
 *     rate IS the rank-score row rather than a sum of the rows above.
 *   - It does not parse markup. Chunk text arrives as plain text, so the query
 *     terms are marked by splitting the string (`utils/highlight-terms.ts`) and
 *     rendering segments — never through `dangerouslySetInnerHTML`.
 */
export function SearchResultDetailDialog({
  hit,
  scoring,
  open,
  onOpenChange
}: SearchResultDetailDialogProps) {
  const t = useTranslations('search');
  // The card mounts this dialog only while it is open, so the selection resets
  // with the unmount — the chunk ids are per document and must not carry over
  // to the next result.
  const [activeChunkId, setActiveChunkId] = useState<string | null>(null);

  const evidence = useMemo(
    () => orderBySimilarity(hit?.chunk_evidence ?? []),
    [hit]
  );

  const matchedTokens = hit?.score_breakdown?.keyword?.matched_tokens ?? [];

  // The badge only means something when there is another passage it outranks.
  const topChunkId = useMemo(() => {
    if (evidence.length < 2) return null;
    const best = evidence[0];
    return best.similarity != null ? chunkKey(best, 0) : null;
  }, [evidence]);

  const identifier =
    hit?.title ?? hit?.container_number ?? hit?.identifiers?.[0] ?? null;

  const { meters, finalPercent, finalSourceKind } = buildRateMeters(
    hit ?? { entity_type: null, entity_id: null },
    scoring
  );
  const explanation = buildScoreExplanation(
    hit ?? { entity_type: null, entity_id: null },
    scoring
  );

  // A container/box matched on its OWN record fields: there is no document
  // behind it, so there are no passages and no page. The dialog drops to one
  // narrow column instead of framing two large empty panels — an empty viewer
  // beside an empty passage list reads as a failed load, not as an answer.
  const isRecordMatch =
    hit?.entity_type !== 'ocr_result' || hit.entity_id == null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 p-0',
          isRecordMatch
            ? 'max-h-[85vh] sm:max-w-lg'
            : 'h-[88vh] max-h-[88vh] sm:max-w-6xl'
        )}
      >
        <DialogHeader className='border-b px-5 py-3 text-start'>
          <DialogTitle className='flex items-center gap-2 text-sm'>
            <FileText className='text-primary size-4 shrink-0' />
            {isRecordMatch
              ? t('matchDetail.recordTitle')
              : identifier
                ? t('matchDetail.title', { identifier })
                : t('matchDetail.titleNoIdentifier')}
          </DialogTitle>
          <DialogDescription className='text-xs'>
            {isRecordMatch ? (
              (identifier ?? t('matchDetail.titleNoIdentifier'))
            ) : (
              <>
                {t('viewerMatchHint', { count: evidence.length })}
                {hit?.ocr_confidence != null && (
                  <>
                    {' · '}
                    {t('ocrConfidence', {
                      value: Math.round(hit.ocr_confidence)
                    })}
                  </>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col lg:flex-row'>
          <div
            className={cn(
              'flex min-h-0 flex-col gap-5 overflow-y-auto px-5 py-4',
              !isRecordMatch && 'lg:w-[45%] lg:shrink-0 lg:border-e'
            )}
          >
            {isRecordMatch ? (
              <RecordMatchSection hit={hit} />
            ) : (
              <section className='flex flex-col gap-2'>
                <h4 className='text-muted-foreground flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase'>
                  <Quote className='size-3' />
                  {t('matchDetail.chunksHeading')}
                  <Badge variant='secondary' className='font-mono text-[10px]'>
                    {evidence.length}
                  </Badge>
                </h4>

                {evidence.length === 0 ? (
                  <p className='text-muted-foreground rounded-md border border-dashed px-3 py-3 text-xs'>
                    {t('explain.noSources')}
                  </p>
                ) : (
                  <ul className='flex flex-col gap-2'>
                    {evidence.map((chunk, i) => (
                      <ChunkCard
                        key={chunkKey(chunk, i)}
                        chunk={chunk}
                        tokens={matchedTokens}
                        isTop={chunkKey(chunk, i) === topChunkId}
                        isActive={
                          chunk.chunk_id != null &&
                          chunk.chunk_id === activeChunkId
                        }
                        onSelect={
                          chunk.chunk_id
                            ? () => setActiveChunkId(chunk.chunk_id)
                            : undefined
                        }
                      />
                    ))}
                  </ul>
                )}

                <p className='text-muted-foreground flex items-center gap-1.5 text-[11px]'>
                  <Lock className='size-3 shrink-0' />
                  {t('matchDetail.permissionNote')}
                </p>
              </section>
            )}

            <section className='flex flex-col gap-3'>
              <h4 className='flex items-center gap-1.5 text-xs font-semibold'>
                <Sparkles className='text-muted-foreground size-3' />
                {finalPercent != null
                  ? t('matchDetail.rateQuestion', {
                      percent: Math.round(finalPercent)
                    })
                  : t('explain.rateHeading')}
              </h4>

              {explanation.rankingKey !== null && (
                <p className='text-muted-foreground text-xs leading-relaxed'>
                  {t(
                    `explain.${explanation.rankingKey}`,
                    explanation.rankingValues
                  )}
                </p>
              )}

              {meters.length === 0 ? (
                <p className='text-muted-foreground text-xs'>
                  {t('explain.noBreakdown')}
                </p>
              ) : (
                <ul className='flex flex-col gap-2'>
                  {meters.map((meter) => (
                    <MeterRow key={meter.kind} meter={meter} />
                  ))}
                </ul>
              )}

              {finalPercent != null && (
                <div className='flex flex-col gap-1.5 border-t pt-3'>
                  <div className='flex items-center gap-3 text-xs'>
                    <span className='w-28 shrink-0 font-semibold'>
                      {t('matchDetail.finalRate')}
                    </span>
                    <Meter
                      percent={finalPercent}
                      className='bg-emerald-500 dark:bg-emerald-400'
                    />
                    <span className='w-24 shrink-0 text-end font-semibold text-emerald-700 tabular-nums dark:text-emerald-300'>
                      {t('matchDetail.percentValue', {
                        percent: Math.round(finalPercent)
                      })}
                    </span>
                  </div>
                  <p className='text-muted-foreground text-[11px] leading-relaxed'>
                    {finalSourceKind === 'rank'
                      ? t('matchDetail.rateIsRankScore')
                      : t('matchDetail.rateSourceUnstated')}
                  </p>
                </div>
              )}

              {meters.some((meter) => !meter.rankingFactor) && (
                <p className='text-muted-foreground text-[11px] leading-relaxed'>
                  {t('matchDetail.ocrNotRanked')}
                </p>
              )}
            </section>
          </div>

          {!isRecordMatch && (
            <DocumentPagePanel
              hit={hit}
              open={open}
              evidence={evidence}
              activeChunkId={activeChunkId}
              onChunkSelect={setActiveChunkId}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What a container/box hit actually matched: its own record fields. There is no
 * document behind it, so instead of an empty passage list the panel shows the
 * text the engine marked and names the indexed fields it matched in.
 *
 * The snippet is the engine's, `<mark>` and all — the one string in this
 * feature that goes through the sanitiser, exactly as the collapsed card does.
 */
function RecordMatchSection({ hit }: { hit: SearchHit | null }) {
  const t = useTranslations('search');
  const fields = hit?.score_breakdown?.keyword?.fields ?? [];

  return (
    <section className='flex flex-col gap-2'>
      <h4 className='text-muted-foreground flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase'>
        <Quote className='size-3' />
        {t('matchDetail.recordMatchHeading')}
      </h4>

      {hit?.snippet ? (
        <p
          className='bg-muted/30 text-foreground/80 rounded-md border px-3 py-2 text-xs leading-relaxed break-words'
          dangerouslySetInnerHTML={{
            __html: purifySearchSnippet(hit.snippet)
          }}
        />
      ) : (
        <p className='text-muted-foreground rounded-md border border-dashed px-3 py-3 text-xs'>
          {t('matchDetail.recordMatchNoSnippet')}
        </p>
      )}

      {fields.length > 0 && (
        <div className='flex flex-wrap items-center gap-1.5'>
          <span className='text-muted-foreground text-[11px]'>
            {t('explain.matchedInFields')}
          </span>
          {fields.map((name) => (
            <Badge key={name} variant='outline' className='text-[10px]'>
              {t.has(`explain.field.${name}`)
                ? t(`explain.field.${name}`)
                : name}
            </Badge>
          ))}
        </div>
      )}

      <p className='text-muted-foreground text-[11px] leading-relaxed'>
        {t('matchDetail.recordMatchNote')}
      </p>
    </section>
  );
}

/**
 * The page half: the document itself, with every matched passage outlined and
 * the selected one spotlighted.
 *
 * Only an `ocr_result` hit has pages to show. A registration or box matched on
 * its record fields, so there is no page image to draw and the column says so
 * rather than rendering an empty frame that reads as a failed load.
 */
function DocumentPagePanel({
  hit,
  open,
  evidence,
  activeChunkId,
  onChunkSelect
}: {
  hit: SearchHit | null;
  open: boolean;
  evidence: SearchChunkEvidence[];
  activeChunkId: string | null;
  onChunkSelect: (chunkId: string) => void;
}) {
  const t = useTranslations('search');
  const ocrResultId =
    hit?.entity_type === 'ocr_result' && hit.entity_id != null
      ? hit.entity_id
      : null;

  const { data: response, isPending } = useQuery({
    ...ocrQueries.result(ocrResultId ?? 0),
    enabled: open && ocrResultId != null
  });
  const result = response?.data;

  const viewerPages: ViewerPage[] = useMemo(() => {
    if (!result) return [];
    // The pipeline always populates `pages`; the single-page legacy fallback
    // mirrors what the OCR screens do so older results still render.
    const fallback = legacyViewerPage(result);
    const pages =
      result.pages && result.pages.length > 0
        ? result.pages
        : fallback
          ? [fallback]
          : [];

    return pages.map((p, index) => {
      const pageChunks = (result.chunks ?? []).filter(
        (c) => c.page_number === index + 1
      );
      // Local disk emits `image_url: null` — fall back to the authenticated
      // streaming endpoint so the viewer works on either storage backend.
      const authEndpoint =
        result.batch_id != null && (p.image_url || p.has_image)
          ? API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(result.batch_id, p.id)
          : null;
      return {
        imageUrl: authEndpoint ? null : p.image_url,
        authEndpoint,
        label: p.page_number
          ? t('pageLabelInDialog', { number: p.page_number })
          : `#${p.id}`,
        chunks: pageChunks,
        isProcessing: false
      };
    });
  }, [result, t]);

  // Every passage behind the match is outlined; the selected one is spotlighted.
  const highlightedChunkIds = useMemo(() => {
    const fromEvidence = evidence
      .map((chunk) => chunk.chunk_id)
      .filter((id): id is string => typeof id === 'string' && id !== '');
    return Array.from(
      new Set([...(hit?.matched_chunk_ids ?? []), ...fromEvidence])
    );
  }, [evidence, hit]);

  // Land on the selected passage's page, else on the first matched one.
  const activePageIndex = useMemo(() => {
    if (viewerPages.length === 0) return 0;
    const selected = activeChunkId
      ? (result?.chunks ?? []).find((c) => c.chunk_id === activeChunkId)
      : null;
    if (selected) return Math.max(0, selected.page_number - 1);

    const firstMatch = (result?.chunks ?? []).find((c) =>
      highlightedChunkIds.includes(c.chunk_id)
    );
    if (firstMatch) return Math.max(0, firstMatch.page_number - 1);

    const fromHit = hit?.page_number != null ? hit.page_number - 1 : -1;
    return fromHit >= 0 && fromHit < viewerPages.length ? fromHit : 0;
  }, [viewerPages.length, activeChunkId, result, highlightedChunkIds, hit]);

  const pageBadge = `PG-${String(activePageIndex + 1).padStart(3, '0')}`;

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-2 px-4 py-4'>
      <div>
        <Badge variant='secondary' className='font-mono text-[10px]'>
          {t('matchDetail.pageBadge', { page: pageBadge })}
        </Badge>
      </div>

      <div className='min-h-0 flex-1'>
        {isPending ? (
          <div className='text-muted-foreground flex h-full items-center justify-center gap-2 text-sm'>
            <Loader2 className='size-4 animate-spin' />
            {t('loading')}
          </div>
        ) : viewerPages.length === 0 ? (
          <div className='text-muted-foreground flex h-full items-center justify-center rounded-md border border-dashed px-6 text-center text-xs'>
            {t('viewerNoPages')}
          </div>
        ) : (
          <DocumentPagesViewer
            pages={viewerPages}
            pdfUrl={result?.pdf_url ?? null}
            highlightedChunkIds={highlightedChunkIds}
            activeChunkIds={activeChunkId ? [activeChunkId] : []}
            activePage={activePageIndex}
            onChunkClick={onChunkSelect}
          />
        )}
      </div>
    </div>
  );
}

/**
 * One passage, with the query's own terms marked inside it. `chunk_id` is shown
 * because it is the identifier the page viewer and the backend both use for
 * this passage — an operator reporting "chunk_012 is wrong" names the same row
 * everyone else can look up.
 */
function ChunkCard({
  chunk,
  tokens,
  isTop,
  isActive,
  onSelect
}: {
  chunk: SearchChunkEvidence;
  tokens: string[];
  isTop: boolean;
  isActive: boolean;
  onSelect?: () => void;
}) {
  const t = useTranslations('search');
  const segments = useMemo(
    () => highlightSegments(chunk.snippet ?? '', tokens),
    [chunk.snippet, tokens]
  );

  const body = (
    <>
      <div className='text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]'>
        {chunk.chunk_id && (
          <Badge variant='outline' className='font-mono text-[10px]'>
            {chunk.chunk_id}
          </Badge>
        )}
        {chunk.page_number != null && (
          <span className='inline-flex items-center gap-1 font-medium'>
            <FileText className='size-3' />
            {chunk.page_to != null && chunk.page_to !== chunk.page_number
              ? t('explain.pageRange', {
                  from: chunk.page_number,
                  to: chunk.page_to
                })
              : t('pageLabelInDialog', { number: chunk.page_number })}
          </span>
        )}
        {chunk.field_label && (
          <Badge variant='outline' className='text-[10px]'>
            {chunk.field_label}
          </Badge>
        )}
        {chunk.similarity != null && (
          <Badge variant='secondary' className='text-[10px] tabular-nums'>
            {t('matchDetail.chunkSimilarity', {
              value: (chunk.similarity / 100).toFixed(2)
            })}
          </Badge>
        )}
        {isTop && (
          <Badge className='border-amber-500/30 bg-amber-500/15 text-[10px] text-amber-700 hover:bg-amber-500/15 dark:text-amber-300'>
            {t('matchDetail.topChunkReason')}
          </Badge>
        )}
      </div>

      <p className='text-foreground/80 text-xs leading-relaxed break-words whitespace-pre-line'>
        {segments.length === 0
          ? t('explain.emptySnippet')
          : segments.map((segment, i) =>
              segment.match ? (
                <mark
                  key={i}
                  className='rounded-[2px] bg-amber-300/70 px-0.5 text-inherit dark:bg-amber-500/40'
                >
                  {segment.text}
                </mark>
              ) : (
                <span key={i}>{segment.text}</span>
              )
            )}
      </p>
    </>
  );

  const className = cn(
    'flex flex-col gap-1.5 rounded-md border px-3 py-2 text-start transition-colors',
    isActive ? 'border-primary bg-primary/5' : 'bg-muted/30'
  );

  // A passage with no id cannot be pointed at on the page, so it stays a plain
  // list item rather than a control that would do nothing when clicked.
  return (
    <li>
      {onSelect ? (
        <button
          type='button'
          onClick={onSelect}
          aria-pressed={isActive}
          className={cn(
            className,
            'hover:border-primary/50 focus-visible:outline-primary w-full focus-visible:outline-2 focus-visible:outline-offset-2'
          )}
        >
          {body}
        </button>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  );
}

/** One factor of the rate: label, bar (when the factor has a scale), value. */
function MeterRow({ meter }: { meter: RateMeter }) {
  const t = useTranslations('search');

  return (
    <li className='flex items-center gap-3 text-xs'>
      <span className='text-muted-foreground w-28 shrink-0'>
        {t(`explain.${meter.labelKey}`)}
      </span>
      {meter.percent != null ? (
        <Meter percent={meter.percent} className='bg-primary' />
      ) : (
        // No bar rather than a bar of an invented length: this factor is not on
        // a 0–100 scale at all.
        <span className='border-muted-foreground/30 h-2 flex-1 rounded-full border border-dashed' />
      )}
      <span className='w-24 shrink-0 text-end tabular-nums'>
        {t(`explain.${meter.valueKey}`, meter.values)}
      </span>
    </li>
  );
}

function Meter({
  percent,
  className
}: {
  percent: number;
  className?: string;
}) {
  return (
    <span className='bg-muted h-2 flex-1 overflow-hidden rounded-full'>
      <span
        className={cn('block h-full rounded-full', className)}
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    </span>
  );
}

/**
 * Strongest passage first, as the ranking reason badge claims. Passages the
 * vector half never scored carry no similarity and keep their server order at
 * the end rather than being sorted as if they scored 0.
 */
function orderBySimilarity(
  evidence: SearchChunkEvidence[]
): SearchChunkEvidence[] {
  return evidence
    .map((chunk, index) => ({ chunk, index }))
    .sort((a, b) => {
      const left = a.chunk.similarity;
      const right = b.chunk.similarity;
      if (left == null && right == null) return a.index - b.index;
      if (left == null) return 1;
      if (right == null) return -1;
      return right - left || a.index - b.index;
    })
    .map((entry) => entry.chunk);
}

function chunkKey(chunk: SearchChunkEvidence, index: number): string {
  return `${chunk.chunk_id ?? 'chunk'}-${index}`;
}
